/**
 * Shared locale-to-URL-prefix mapping.
 * Used by metadata, sitemap, middleware, and navigation.
 *
 * en (US) has no prefix → /about-us
 * en-GB uses /gb → /gb/about-us
 * Other locales use their code → /de/about-us
 */

export const LOCALE_PREFIX: Record<string, string> = {
  en: "",
  "en-GB": "/gb",
  "en-IE": "/ie",
  "en-AU": "/au",
  de: "/de",
  es: "/es",
  nl: "/nl",
};

/** All supported locales including en-GB */
export const ALL_LOCALES = [
  "en",
  "en-GB",
  "en-IE",
  "en-AU",
  "de",
  "es",
  "nl",
] as const;

/** Locales that have their own content directories (variant locales like en-GB fall back to en) */
export const CONTENT_LOCALES = ["en", "de", "es", "nl"] as const;

/**
 * Maps internal locale codes to hreflang attribute values.
 * en → en-US (not just "en") to distinguish from en-GB.
 */
export const LOCALE_HREFLANG: Record<string, string> = {
  en: "en-US",
  "en-GB": "en-GB",
  "en-IE": "en-IE",
  "en-AU": "en-AU",
  de: "de",
  es: "es",
  nl: "nl",
};

/**
 * BCP 47 locale for `Intl.NumberFormat` currency output.
 * `en-AU` renders AUD as bare "$" which is the desired format.
 */
export function intlCurrencyFormatLocale(locale: string): string {
  return locale;
}

/** Maps locale to ISO currency code. Single source of truth for locale→currency. */
export const LOCALE_CURRENCY: Record<string, string> = {
  en: "USD",
  "en-GB": "GBP",
  "en-IE": "EUR",
  "en-AU": "AUD",
  de: "EUR",
  es: "EUR",
  nl: "EUR",
};

/** Maps locale to currency symbol for display. Derived from LOCALE_CURRENCY. */
export const LOCALE_CURRENCY_SYMBOL: Record<string, string> = {
  en: "$",
  "en-GB": "£",
  "en-IE": "€",
  "en-AU": "$",
  de: "€",
  es: "€",
  nl: "€",
};

/** Locales available for US-only pages (no en-GB / /gb variant) */
export const US_ONLY_LOCALES = ["en", "de", "es", "nl"] as const;

/** Check if a locale can access US-only pages. Call with notFound() for disallowed locales. */
export function isLocaleAllowedForUsOnly(locale: string): boolean {
  return (US_ONLY_LOCALES as readonly string[]).includes(locale);
}

/** Locales available for English-only pages (no de/es/nl variants) */
export const EN_ONLY_LOCALES = ["en", "en-GB", "en-IE", "en-AU"] as const;

/** Check if a locale can access English-only pages. Call with notFound() for disallowed locales. */
export function isLocaleAllowedForEnOnly(locale: string): boolean {
  return (EN_ONLY_LOCALES as readonly string[]).includes(locale);
}

/** Locales available for GB-only pages (competitor unknown outside the UK market) */
export const GB_ONLY_LOCALES = ["en-GB"] as const;

/** Check if a locale can access GB-only pages. Call with notFound() for disallowed locales. */
export function isLocaleAllowedForGbOnly(locale: string): boolean {
  return (GB_ONLY_LOCALES as readonly string[]).includes(locale);
}

/** Page slugs that are US-only (no /gb variant). Single source of truth. */
export const US_ONLY_SLUGS = new Set([
  // US-only competitor comparisons (UK users won't recognise these brands)
  "compare-payability",
  "compare-comp-settle",
  "compare-8fig",
]);

/** Page slugs that are GB-only (no en/de/es/nl/ie/au variants). Single source of truth. */
export const GB_ONLY_SLUGS = new Set([
  // GB-only competitor comparisons (iwoca only lends to UK-registered businesses)
  "compare-iwoca",
]);

/**
 * Blog post slugs with `usOnly: true` in frontmatter (content/blog/en/*.mdx)
 * and no localised variant in any English-variant locale.
 * Kept in sync manually — middleware runs before content is loaded, so it
 * can't read frontmatter directly. Used to stop geo-redirect and locale
 * routing from sending non-US visitors to a /gb, /ie or /au URL that 404s.
 */
export const US_ONLY_BLOG_SLUGS = new Set([
  "business-loan-calculator",
  "build-business-credit-low-personal-score",
  "growth-lever-shipping-returns-policy",
]);

/**
 * Blog post slugs that have a real `content/blog/en-GB/*.mdx` localisation
 * but no en-IE or en-AU variant yet (the US file stays `usOnly: true` so it
 * doesn't leak into those locales via fallback). Geo-redirect and locale
 * routing should allow en-GB through to /gb but keep blocking en-IE/en-AU.
 */
export const GB_LOCALIZED_ONLY_BLOG_SLUGS = new Set([
  "how-to-get-a-business-loan",
]);

/** Page slugs that exist only in English locales (no de/es/nl translations). */
export const EN_ONLY_SLUGS = new Set([
  // Clearco funds US-incorporated businesses only, but UK search demand and
  // en-IE/en-AU fallback content still justify the English-locale variants.
  "compare-clearco",
  "broker",
  "flexible-funding",
  "restaurant-business-loans",
  "black-friday",
  "payment-holiday",
  "tools",
  "wayflyer-intelligence",
  "dynamic-funding",
  "link-my-books",
  "always-on-funding",
  "alternative-business-loans",
  "cult-brands-business-case",
  "figma-comparison-test",
  "invoice-financing-vs-factoring",
  "scaling-a-winning-campaign",
  "the-accounting-data-advantage",
  "what-is-working-capital",
]);

/** Resolve the locales a given page slug supports. Defaults to all locales. */
export function getLocalesForSlug(slug: string): readonly string[] {
  if (US_ONLY_SLUGS.has(slug)) return US_ONLY_LOCALES;
  if (EN_ONLY_SLUGS.has(slug)) return EN_ONLY_LOCALES;
  if (GB_ONLY_SLUGS.has(slug)) return GB_ONLY_LOCALES;
  return ALL_LOCALES;
}

/** Maps locale to Open Graph locale format (underscore convention). */
export const LOCALE_OG: Record<string, string> = {
  en: "en_US",
  "en-GB": "en_GB",
  "en-IE": "en_IE",
  "en-AU": "en_AU",
  de: "de_DE",
  es: "es_ES",
  nl: "nl_NL",
};

/**
 * Build a locale-prefixed path.
 * e.g., buildLocalePath("en-GB", "/about-us") → "/gb/about-us"
 *       buildLocalePath("en", "/about-us") → "/about-us"
 *       buildLocalePath("en", "") → "/"  (homepage)
 */
export function buildLocalePath(locale: string, path: string): string {
  const prefix = LOCALE_PREFIX[locale] ?? "";
  // Normalize bare "/" to empty string so prefixed locales don't get a trailing slash
  // e.g. buildLocalePath("en-GB", "/") → "/gb" not "/gb/"
  const normalizedPath = path === "/" ? "" : path;
  if (!normalizedPath) return prefix || "/";
  return `${prefix}${normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`}`;
}
